import { TrashIcon } from '@heroicons/react/24/outline';

import { type ToolbarAction } from '@/components/core/ToolbarTable';
import { AUTH_OBJECT_TYPES, useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';

import {
    cleanKeys,
    type DeletePermissionItem,
    deletePermissionWarning,
    deleteRequestForSelection,
} from './deleteItemsSelection';
import { useDeleteItemsConfirm } from './useDeleteItemsConfirm.tsx';

type BrowseListSelection = {
    selectedItems: DeletePermissionItem[];
    loadedItems: DeletePermissionItem[];
    allItemsSelected: boolean;
    watchlistPrimaryKeys: string[];
};

type BrowseDeleteActionInput = BrowseListSelection & {
    itemsQuery?: Record<string, unknown>;
    routeCategory: string | null;
    routeSelectionId?: unknown;
    pauseEditing?: () => void;
    /** The toolbar keeps Delete visible, and locked, while nothing is selected. */
    showWithoutSelection?: boolean;
};

const EMPTY_BROWSE_SELECTION: BrowseListSelection = {
    selectedItems: [],
    loadedItems: [],
    allItemsSelected: false,
    watchlistPrimaryKeys: [],
};

const useBrowseDeleteAction = ({
    selectedItems,
    loadedItems,
    allItemsSelected,
    watchlistPrimaryKeys,
    itemsQuery,
    routeCategory,
    routeSelectionId,
    pauseEditing,
    showWithoutSelection = false,
}: BrowseDeleteActionInput) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { auth } = useAuth();
    const { requestDelete, dialog } = useDeleteItemsConfirm();

    const canDeleteItemsUi = !!auth?.hasAnyPermission(
        AUTH_OBJECT_TYPES.OBJECT_TYPE_UI,
        'delete.items',
    );
    const canDeleteCategory = !!auth?.hasAnyPermission(
        AUTH_OBJECT_TYPES.OBJECT_TYPE_ITEMS,
        'delete',
        { category__: routeCategory } as unknown as Record<string, string>,
    );
    const selectedKeys = cleanKeys(selectedItems.map((item) => item.primaryKey__ ?? ''));
    const hasSelection = allItemsSelected || selectedKeys.length > 0;
    const visible = canDeleteItemsUi && canDeleteCategory && (hasSelection || showWithoutSelection);

    const onClick = () => {
        if (!hasSelection) {
            toast.warning('NO_ITEMS_SELECTED');
            return;
        }

        pauseEditing?.();

        const rowsToCheck = allItemsSelected ? loadedItems : selectedItems;
        const denied = rowsToCheck.find((item) => !auth?.hasItemPermission('delete', item));
        if (denied) {
            toast.warning(deletePermissionWarning(t, denied));
            return;
        }

        const request = deleteRequestForSelection({
            allItemsSelected,
            selectedKeys,
            routeSelectionId,
            watchlistPrimaryKeys,
            itemsQuery,
        });
        if (!request) {
            return;
        }
        requestDelete(request);
    };

    const action: ToolbarAction | null = visible
        ? {
            id: 'delete',
            icon: TrashIcon,
            label: t('DELETE_ITEMS'),
            variant: 'ghostDestructive',
            disabled: false,
            onClick,
        }
        : null;

    return { action, dialog };
};

export type { BrowseDeleteActionInput, BrowseListSelection };
export { EMPTY_BROWSE_SELECTION, useBrowseDeleteAction };
